import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { useCart } from "../../context/CartContext";
import { calculateCombinedReservationDuration } from "../../utils/bookingDuration";
import { encodeBookingGroups } from "../../utils/bookingPlan";

const money = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});

const surface = {
  border: "1px solid #e8e5df",
  borderRadius: { xs: 3, md: 4 },
  boxShadow: "0 16px 45px rgba(24, 24, 22, 0.06)",
  backgroundColor: "#fff",
};

const CartPage = () => {
  const navigate = useNavigate();
  const { cartGroups, removeBookingGroup, clearCart, cartCount, refreshCartAssignments } = useCart();

  const hasUnavailableAssignments = cartGroups.some((group) =>
    (group.assignments || []).some((assignment) => assignment.available === false)
  );

  const totalReservationAmount = cartGroups.reduce(
    (total, group) => total + (group.assignments || []).reduce(
      (groupTotal, assignment) => groupTotal + Number(assignment.service?.price || 0),
      0
    ),
    0
  );

  const totalDuration = cartGroups.reduce((total, group) => {
    const assignments = group.assignments || [];
    const services = assignments.map((assignment) => ({
      ...assignment.service,
      categoryId: assignment.categoryId ?? assignment.service?.categoryId,
    }));
    return total + calculateCombinedReservationDuration(services, assignments);
  }, 0);

  const handleContinue = () => {
    const firstAssignment = cartGroups[0]?.assignments?.[0];
    if (!firstAssignment || hasUnavailableAssignments) return;

    navigate(
      `/reservar/${firstAssignment.service.id}/${firstAssignment.employee.id}?bookingGroups=${encodeBookingGroups(cartGroups)}&fromCart=1`
    );
  };

  return (
    <>
      <Navbar />
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f7f6f3" }}>
        <Box component="main" sx={{ flexGrow: 1, pt: { xs: 5, md: 8 }, pb: { xs: 7, md: 11 }, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ maxWidth: 1180, mx: "auto" }}>
            <Box sx={{ mb: { xs: 4, md: 5 } }}>
              <Typography sx={{ color: "#8a6d3b", fontSize: 12, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", mb: 1 }}>
                Tu próxima visita
              </Typography>
              <Typography component="h1" sx={{ fontSize: { xs: 34, md: 48 }, lineHeight: 1.08, fontWeight: 800, letterSpacing: "-1.5px", color: "#181816" }}>
                Carrito de servicios
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 620, fontSize: { xs: 15, md: 17 } }}>
                Revisá tu selección y continuá para elegir la fecha y el horario que mejor te queden.
              </Typography>
            </Box>

            {!cartGroups.length ? (
              <Card sx={{ ...surface, maxWidth: 720, mx: "auto", textAlign: "center" }}>
                <CardContent sx={{ py: { xs: 7, md: 10 }, px: 3 }}>
                  <Box sx={{ width: 84, height: 84, borderRadius: "50%", bgcolor: "#f1eee7", display: "grid", placeItems: "center", mx: "auto", mb: 3 }}>
                    <ShoppingCartOutlinedIcon sx={{ fontSize: 38, color: "#8a6d3b" }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Tu carrito está vacío</Typography>
                  <Typography color="text.secondary" sx={{ mb: 4 }}>Elegí los servicios que quieras reservar y los vas a encontrar acá.</Typography>
                  <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/")} sx={{ bgcolor: "#181816", borderRadius: 2.5, px: 3.5, py: 1.35, textTransform: "none", fontWeight: 700, "&:hover": { bgcolor: "#343430" } }}>
                    Explorar servicios
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 360px" }, gap: { xs: 3, md: 4 }, alignItems: "start" }}>
                <Stack spacing={2.5}>
                  {cartGroups.map((group, index) => {
                    const assignments = group.assignments || [];
                    const groupAmount = assignments.reduce((total, assignment) => total + Number(assignment.service?.price || 0), 0);
                    const groupDuration = calculateCombinedReservationDuration(
                      assignments.map((assignment) => ({ ...assignment.service, categoryId: assignment.categoryId ?? assignment.service?.categoryId })),
                      assignments
                    );

                    return (
                      <Card key={group.id} sx={surface}>
                        <CardContent sx={{ p: { xs: 2.25, sm: 3 }, "&:last-child": { pb: { xs: 2.25, sm: 3 } } }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2.5 }}>
                            <Box>
                              <Chip label={`BLOQUE ${index + 1}`} size="small" sx={{ bgcolor: "#f1eee7", color: "#725929", fontWeight: 800, letterSpacing: 1, fontSize: 10, mb: 1.25 }} />
                              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                                {assignments.map((assignment) => assignment.service?.name).filter(Boolean).join(" + ")}
                              </Typography>
                              <Stack direction="row" spacing={0.7} alignItems="center" sx={{ color: "text.secondary", mt: 0.75 }}>
                                <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />
                                <Typography variant="body2">Duración estimada: {groupDuration} min</Typography>
                              </Stack>
                            </Box>
                            <IconButton aria-label={`Quitar bloque ${index + 1}`} onClick={() => removeBookingGroup(group.id)} sx={{ color: "#a34747", bgcolor: "#fff3f1", "&:hover": { bgcolor: "#ffe6e2" } }}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>

                          <Stack spacing={1.25}>
                            {assignments.map((assignment) => (
                              <Box key={`${group.id}-${assignment.service.id}-${assignment.employee.id}`} sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", alignItems: "center", gap: { xs: 1.25, sm: 2 }, p: { xs: 1.5, sm: 2 }, bgcolor: "#faf9f7", border: "1px solid #eeeae4", borderRadius: 2.5 }}>
                                <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: "#181816", color: "white", display: "grid", placeItems: "center" }}>
                                  <EventAvailableOutlinedIcon sx={{ fontSize: 21 }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 750, lineHeight: 1.25 }}>{assignment.service.name}</Typography>
                                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary", mt: 0.5 }}>
                                    <PersonOutlineIcon sx={{ fontSize: 17 }} />
                                    <Typography variant="body2" noWrap>{assignment.employee.name}</Typography>
                                  </Stack>
                                  {assignment.available === false && <Chip label="No disponible" size="small" color="error" sx={{ mt: 1, fontWeight: 700 }} />}
                                </Box>
                                <Typography sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{money.format(Number(assignment.service?.price || 0))}</Typography>
                              </Box>
                            ))}
                          </Stack>

                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2.5, pt: 2, borderTop: "1px solid #eeeae4" }}>
                            <Typography color="text.secondary" variant="body2">Subtotal del bloque</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{money.format(groupAmount)}</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}

                  <Button color="inherit" startIcon={<DeleteOutlineIcon />} onClick={clearCart} sx={{ alignSelf: "flex-start", color: "text.secondary", textTransform: "none", fontWeight: 650 }}>
                    Vaciar carrito
                  </Button>
                </Stack>

                <Card sx={{ ...surface, position: { md: "sticky" }, top: { md: 112 } }}>
                  <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2.5 }}>Resumen de reserva</Typography>
                    <Stack spacing={1.6}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography color="text.secondary">Servicios</Typography><Typography fontWeight={700}>{cartCount}</Typography></Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography color="text.secondary">Duración estimada</Typography><Typography fontWeight={700}>{totalDuration} min</Typography></Box>
                    </Stack>
                    <Divider sx={{ my: 2.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
                      <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                      <Typography sx={{ fontSize: 28, fontWeight: 850, letterSpacing: "-1px" }}>{money.format(totalReservationAmount)}</Typography>
                    </Box>

                    {hasUnavailableAssignments && (
                      <Box sx={{ mt: 2.5, p: 1.5, borderRadius: 2, bgcolor: "#fff1f0", color: "#a12d2d" }}>
                        <Typography variant="body2" fontWeight={650}>Hay servicios que ya no están disponibles. Actualizá el carrito antes de continuar.</Typography>
                      </Box>
                    )}

                    <Button fullWidth variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={handleContinue} disabled={hasUnavailableAssignments} sx={{ mt: 3, bgcolor: "#181816", borderRadius: 2.5, py: 1.45, textTransform: "none", fontWeight: 750, fontSize: 15, "&:hover": { bgcolor: "#343430" } }}>
                      Elegir fecha y horario
                    </Button>
                    <Button fullWidth startIcon={<RefreshIcon />} onClick={refreshCartAssignments} sx={{ mt: 1, color: "#575750", textTransform: "none", fontWeight: 650 }}>
                      Actualizar disponibilidad
                    </Button>

                    <Stack spacing={1.2} sx={{ mt: 2.5, pt: 2.5, borderTop: "1px solid #eeeae4" }}>
                      <Stack direction="row" spacing={1} alignItems="center"><CheckCircleOutlineIcon sx={{ color: "#64815f", fontSize: 19 }} /><Typography variant="body2" color="text.secondary">Confirmación inmediata</Typography></Stack>
                      <Stack direction="row" spacing={1} alignItems="center"><LockOutlinedIcon sx={{ color: "#64815f", fontSize: 19 }} /><Typography variant="body2" color="text.secondary">Reserva segura</Typography></Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        </Box>
        <Footer />
      </Box>
    </>
  );
};

export default CartPage;
